import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

import { BasketActionsType } from '../../../types/basket';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import { ShoppingCartCounter, Typography } from '../../atoms';
import { InfoFilled as InfoFilledIcon } from '../../icons';
import { basketUtils } from '../../organisms/Basket/utils/basketUtils';

export interface CheckoutButtonProps {
  actions: BasketActionsType;
  basketItems: BasketControl.Item[];
  buttonLabel: string;
  currency: Api.Currency;
  languageCode: string;
  isCheckoutEnabled: boolean;
  minOrder: number;
  translate: any;
  vrId: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  checkoutButton: {
    backgroundColor: theme.palette.common.white,
    border: 'none',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    bottom: 0,
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: '56px 1fr',
    minHeight: 'unset',
    right: 0,
    padding: theme.spacing(1, 1, 1, 0),
    position: 'fixed',
    width: '100%',
    zIndex: theme.zIndex.appBar,

    '& .buttonState': {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      width: '100%',
    },

    '& .fd-button__primary': {
      padding: theme.spacing(1.5, 2),
    },
  },

  buttonContent: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    width: '100%',

    '& .fd-text em': {
      color: theme.palette.primary.contrastText,
    },

    '.disabled &': {
      backgroundColor: theme.palette.grey[200],

      '& .fd-text__h3': {
        color: theme.palette.grey[800],
      },
    },
  },

  minOrderWarning: {
    alignItems: 'center',
    color: theme.palette.grey[800],
    display: 'flex',
    flexFlow: 'row nowrap',

    '& svg': {
      marginRight: theme.spacing(1),
      color: theme.palette.info.main,
    },
  },

  [theme.breakpoints.up('sm')]: {
    checkoutButton: {
      border: `1px solid ${theme.palette.grey[200]}`,
      borderBottom: 'transparent',
      boxSizing: 'border-box',
      right: theme.spacing(1),
      width: '360px',

      '& .fd-button__primary': {
        padding: theme.spacing(2.5, 3),
      },
    },
  },
}));

export function CheckoutButton(props: CheckoutButtonProps): JSX.Element {
  const {
    actions,
    basketItems,
    buttonLabel,
    currency,
    languageCode,
    isCheckoutEnabled,
    minOrder,
    translate,
  } = props;

  const classes = useStyles();

  const basketTotal = BasketControl.Total(basketItems);
  const basketItemsSummary = basketUtils.summariseBasketItems(basketItems);
  const basketItemCount = Object.values(basketItemsSummary).reduce(
    (acc, basketItem) => {
      // eslint-disable-next-line no-param-reassign
      acc += basketItem.quantity;
      return acc;
    },
    0,
  );
  const isOrderSufficient = basketTotal >= minOrder;
  const localisedBasketTotal = BasketControl.ToCurrency(
    basketTotal,
    currency,
    languageCode,
  );
  const localisedMinOrder = BasketControl.ToCurrency(
    minOrder,
    currency,
    languageCode,
  );

  let buttonContent = null;

  if (isOrderSufficient) {
    buttonContent = (
      <Typography variant="h3">
        <em>{buttonLabel}</em>
      </Typography>
    );
  } else {
    buttonContent = (
      <div className={classes.minOrderWarning}>
        <InfoFilledIcon size="xs" />
        <Typography variant="body2">
          {translate('Menu_Basket_MinimumOrder')} {localisedMinOrder}
        </Typography>
      </div>
    );
  }

  const isEnabled = isOrderSufficient && isCheckoutEnabled;
  const checkoutButtonStyles = clsx(
    classes.checkoutButton,
    'fd-button fd-button__primary',
    !isEnabled && 'disabled',
  );
  const itemCounterStyles = clsx(classes.itemCounter, !isEnabled && 'disabled');

  return (
    <button
      className={checkoutButtonStyles}
      disabled={!isEnabled}
      onClick={actions.onCheckout}
      type="button"
    >
      <ShoppingCartCounter
        count={basketItemCount}
        className={itemCounterStyles}
        size="m"
      />

      <div className={classes.buttonContent}>
        {buttonContent}

        <Typography variant="h3">
          <em>{localisedBasketTotal}</em>
        </Typography>
      </div>
    </button>
  );
}
